import React, { useState } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Snackbar,
  Alert,
  Box,
  TextField,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import DownloadIcon from "@mui/icons-material/Download";
import SignatureIcon from "@mui/icons-material/Draw";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import CreateIcon from "@mui/icons-material/Create";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BalanceIcon from "@mui/icons-material/Balance";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WarningIcon from "@mui/icons-material/Warning";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useTheme, alpha, styled } from '@mui/material/styles';
import useMediaQuery from "@mui/material/useMediaQuery";
import client from "../../../API";
import useAuth from "../../../Hooks/use-auth";

const MyTeam = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
 
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [tabValue, setTabValue] = useState(0);
 
  // User Action States
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
 
  // Team Management State
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    name: "",
    description: "",
    team_leader_id: "",
    can_manage_members: false
  });
 
  // User Assignment State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTeamForAssignment, setSelectedTeamForAssignment] = useState(null);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState("");
 
  // Spending Power Management State
  const [spendingDialogOpen, setSpendingDialogOpen] = useState(false);
  const [spendingRequestData, setSpendingRequestData] = useState({
    requested_credit_line: "",
    requested_interest_rate: "",
    reason: ""
  });
 
  // Team Spending Power Management State
  const [teamSpendingDialogOpen, setTeamSpendingDialogOpen] = useState(false);
  const [selectedTeamForSpending, setSelectedTeamForSpending] = useState(null);
  const [teamSpendingData, setTeamSpendingData] = useState({
    allocated_credit_line: "",
    reason: ""
  });
 
  // Spending Power Rebalancing State
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [rebalanceData, setRebalanceData] = useState([]);
 
  // Team Spending Usage State
  const [spendingUsageDialogOpen, setSpendingUsageDialogOpen] = useState(false);
  const [spendingUsageData, setSpendingUsageData] = useState({
    amount: "",
    reason: "",
    operation: "use"
  });
 
  // Legal Entity Management State
  const [legalEntityDialogOpen, setLegalEntityDialogOpen] = useState(false);
  const [legalEntityName, setLegalEntityName] = useState("");
 
  // Contract Management State
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractSignatureDialogOpen, setContractSignatureDialogOpen] = useState(false);
 
  /* ─────────────────────────────── FETCH DATA ─────────────────────────────── */
  const {
    data: teamStructure,
    isLoading: teamLoading,
    error: teamError,
  } = useQuery(
    ["myTeam", currentUser?.id],
    client.users.fetchMyTeam,
    {
      staleTime: 180000,   // 3 min (< Backend TTL of 3 min)
      cacheTime: 360000,   // 6 min (> Backend TTL)
      refetchOnWindowFocus: true,  // Team data is important
    }
  );
 
  const {
    data: companyData,
    isLoading: companyLoading,
  } = useQuery(
    ["company", teamStructure?.company_id],
    () => client.companies.listFetch({ id: teamStructure.company_id }),
    { enabled: !!teamStructure?.company_id }
  );
 
  const {
    data: spendingSummary,
    isLoading: spendingSummaryLoading,
  } = useQuery(
    ["spendingSummary", companyData?.id],
    () => client.teams.getSpendingSummary(),
    { enabled: !!companyData?.id }
  );
 
  const {
    data: creditLedger,
    isLoading: ledgerLoading,
  } = useQuery(
    ["creditLedger", companyData?.id],
    () => client.creditLedger.fetch(companyData.id),
    { enabled: !!companyData?.id }
  );
 
  const {
    data: contracts,
    isLoading: contractsLoading,
  } = useQuery(
    ["contracts", companyData?.id],
    async () => {
      const response = await client.contracts.getAssignments({ company_id: companyData.id });
      return response.assignments;
    },
    { enabled: !!companyData?.id }
  );
 
  const {
    data: teamFiles,
    isLoading: filesLoading,
  } = useQuery(
    ["teamFiles", companyData?.id],
    async () => {
      const response = await client.companies.getFiles(companyData.id);
      return response.files;
    },
    { enabled: !!companyData?.id }
  );
 
  /* ─────────────────────────── MUTATIONS ─────────────────────────────── */
  const activateMutation = useMutation(
    (userId) => client.users.confirm(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        setSnack({ open: true, msg: "User activated!", sev: "success" });
      },
      onError: () => {
        setSnack({ open: true, msg: "Activation failed.", sev: "error" });
      },
      onSettled: () => setDialogOpen(false),
    }
  );

  const deactivateUserMutation = useMutation(
    (userId) => client.users.deactivateUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        setDeactivateDialogOpen(false);
        setSelectedMember(null);
        setSnack({ open: true, msg: "User deactivated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ 
          open: true, 
          msg: error?.response?.data?.error || "Failed to deactivate user", 
          sev: "error" 
        });
      },
    }
  );
 
  const createTeamMutation = useMutation(
    (data) => client.teams.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        queryClient.invalidateQueries("spendingSummary");
        setTeamDialogOpen(false);
        resetTeamForm();
        setSnack({ open: true, msg: "Team created successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to create team", sev: "error" });
      },
    }
  );
 
  const updateTeamMutation = useMutation(
    ({ id, data }) => client.teams.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        queryClient.invalidateQueries("spendingSummary");
        setTeamDialogOpen(false);
        resetTeamForm();
        setSnack({ open: true, msg: "Team updated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to update team", sev: "error" });
      },
    }
  );
 
  const assignUserMutation = useMutation(
    ({ teamId, userId }) => client.teams.assignUser(teamId, userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        setAssignDialogOpen(false);
        setSelectedUserForAssignment("");
        setSnack({ open: true, msg: "User assigned to team!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to assign user", sev: "error" });
      },
    }
  );
 
  const removeUserMutation = useMutation(
    ({ teamId, userId }) => client.teams.removeUser(teamId, userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        setRemoveDialogOpen(false);
        setSelectedMember(null);
        setSnack({ open: true, msg: "User removed from team!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to remove user", sev: "error" });
      },
    }
  );
 
  const allocateTeamSpendingMutation = useMutation(
    ({ teamId, data }) => client.teams.allocateSpending(teamId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        queryClient.invalidateQueries("spendingSummary");
        setTeamSpendingDialogOpen(false);
        setTeamSpendingData({ allocated_credit_line: "", reason: "" });
        setSnack({ open: true, msg: "Team spending power allocated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to allocate spending power", sev: "error" });
      },
    }
  );
 
  const rebalanceSpendingMutation = useMutation(
    (data) => client.teams.rebalanceSpending(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        queryClient.invalidateQueries("spendingSummary");
        setRebalanceDialogOpen(false);
        setSnack({ open: true, msg: "Spending power rebalanced successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to rebalance spending power", sev: "error" });
      },
    }
  );
 
  const manageSpendingUsageMutation = useMutation(
    ({ teamId, operation, data }) => {
      if (operation === "use") {
        return client.teams.useSpending(teamId, data);
      } else {
        return client.teams.releaseSpending(teamId, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["myTeam", currentUser?.id]);
        queryClient.invalidateQueries("spendingSummary");
        setSpendingUsageDialogOpen(false);
        setSpendingUsageData({ amount: "", reason: "", operation: "use" });
        setSnack({ open: true, msg: "Spending usage updated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to update spending usage", sev: "error" });
      },
    }
  );
 
  const spendingRequestMutation = useMutation(
    (data) => client.creditLedger.postRequest({
      company_id: companyData.id,
      ...data
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["creditLedger", companyData.id]);
        setSpendingDialogOpen(false);
        setSpendingRequestData({ requested_credit_line: "", requested_interest_rate: "", reason: "" });
        setSnack({ open: true, msg: "Spending power request submitted!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Request failed", sev: "error" });
      },
    }
  );
 
  const updateLegalEntityMutation = useMutation(
    (data) => client.companies.updateLegalEntity(companyData.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["company", companyData.id]);
        setLegalEntityDialogOpen(false);
        setSnack({ open: true, msg: "Legal entity name updated!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Update failed", sev: "error" });
      },
    }
  );
 
  const signContractMutation = useMutation(
    (data) => client.contracts.sign(data.contractId, data.signature),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["contracts", companyData.id]);
        setContractSignatureDialogOpen(false);
        setSnack({ open: true, msg: "Contract signed successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Signing failed", sev: "error" });
      },
    }
  );
 
  /* ───────────────────────────────── HANDLERS ───────────────────────────────── */
  const openDialog = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };
 
  const resetTeamForm = () => {
    setTeamFormData({
      name: "",
      description: "",
      team_leader_id: "",
      can_manage_members: false
    });
    setEditingTeam(null);
  };
 
  const handleCreateTeam = () => {
    setEditingTeam(null);
    resetTeamForm();
    setTeamDialogOpen(true);
  };
 
  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamFormData({
      name: team.name,
      description: team.description || "",
      team_leader_id: team.team_leader_id || "",
      can_manage_members: team.can_manage_members
    });
    setTeamDialogOpen(true);
  };
 
  const handleTeamSubmit = () => {
    if (editingTeam) {
      updateTeamMutation.mutate({ id: editingTeam.id, data: teamFormData });
    } else {
      createTeamMutation.mutate(teamFormData);
    }
  };
 
  const handleAssignUser = (team) => {
    setSelectedTeamForAssignment(team);
    setAssignDialogOpen(true);
  };
 
  const handleAssignSubmit = () => {
    assignUserMutation.mutate({
      teamId: selectedTeamForAssignment.id,
      userId: parseInt(selectedUserForAssignment)
    });
  };
 
  const handleAllocateTeamSpending = (team) => {
    setSelectedTeamForSpending(team);
    setTeamSpendingData({
      allocated_credit_line: team.allocated_credit_line || "",
      reason: ""
    });
    setTeamSpendingDialogOpen(true);
  };
 
  const handleTeamSpendingSubmit = () => {
    allocateTeamSpendingMutation.mutate({
      teamId: selectedTeamForSpending.id,
      data: teamSpendingData
    });
  };
 
  const handleSpendingUsage = (team, operation) => {
    setSelectedTeamForSpending(team);
    setSpendingUsageData({
      amount: "",
      reason: "",
      operation
    });
    setSpendingUsageDialogOpen(true);
  };
 
  const handleSpendingUsageSubmit = () => {
    manageSpendingUsageMutation.mutate({
      teamId: selectedTeamForSpending.id,
      operation: spendingUsageData.operation,
      data: {
        amount: spendingUsageData.amount,
        reason: spendingUsageData.reason
      }
    });
  };
 
  const handleRebalanceSpending = () => {
    if (spendingSummary?.teams) {
      const initialData = spendingSummary.teams.map(team => ({
        team_id: team.id,
        team_name: team.name,
        current_allocation: parseFloat(team.allocated_credit_line),
        allocation: parseFloat(team.allocated_credit_line),
        used_credit: parseFloat(team.used_credit)
      }));
      setRebalanceData(initialData);
      setRebalanceDialogOpen(true);
    }
  };
 
  const handleRebalanceSubmit = () => {
    const team_allocations = rebalanceData.map(item => ({
      team_id: item.team_id,
      allocation: item.allocation
    }));
   
    rebalanceSpendingMutation.mutate({
      team_allocations,
      reason: "Spending power rebalancing operation"
    });
  };
 
  const handleSpendingRequest = () => {
    spendingRequestMutation.mutate(spendingRequestData);
  };
 
  const handleLegalEntityUpdate = () => {
    updateLegalEntityMutation.mutate({ legal_entity_name: legalEntityName });
  };
 
  const handleContractSign = () => {
    const signature = {
      signer_name: `${teamStructure?.all_members?.[0]?.first_name} ${teamStructure?.all_members?.[0]?.last_name}`,
      signer_email: teamStructure?.all_members?.[0]?.email,
      signed_at: new Date().toISOString(),
      ip_address: "system",
    };
    signContractMutation.mutate({ contractId: selectedContract.id, signature });
  };
 
  const downloadContract = (contract) => {
    if (contract.file?.file_url) {
      window.open(contract.file.file_url, '_blank');
    }
  };
 
  // User Action Handlers
  const handleOpenActionMenu = (event, member, team) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedMember({ ...member, teamId: team?.id });
  };
 
  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };
 
  const handleDeactivateUser = () => {
    handleCloseActionMenu();
    setDeactivateDialogOpen(true);
  };
 
  const handleRemoveUserFromTeam = () => {
    handleCloseActionMenu();
    setRemoveDialogOpen(true);
  };
 
  const confirmDeactivateUser = () => {
    if (selectedMember?.id) {
      deactivateUserMutation.mutate(selectedMember.id);
    }
  };
 
  const confirmRemoveUser = () => {
    if (selectedMember?.teamId && selectedMember?.id) {
      removeUserMutation.mutate({
        teamId: selectedMember.teamId,
        userId: selectedMember.id
      });
    }
  };
 
  // Permission Check
  const isTeamManager = (team) => {
    // Check if current user is team leader or has management permissions
    return team?.team_leader_id === currentUser?.id || currentUser?.is_admin;
  };
 
  const getSpendingStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'frozen': return 'error';
      default: return 'default';
    }
  };
 
  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'info';
    return 'success';
  };
 
  /* ───────────────────────────────── RENDER ───────────────────────────────── */
  if (teamLoading || companyLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <CircularProgress size={60} />
    </Box>
  );
 
  if (teamError) return (
    <Alert severity="error" sx={{ m: 3 }}>
      <Typography variant="h6">Error loading team data</Typography>
      <Typography variant="body2">{teamError.message}</Typography>
    </Alert>
  );
 
  const companyName = teamStructure?.all_members?.[0]?.company_name || "";
  const teams = teamStructure?.teams || [];
  const unassignedMembers = teamStructure?.unassigned_members || [];
  const allMembers = teamStructure?.all_members || [];
  const totalAllocationAmount = rebalanceData.reduce((sum, item) => sum + (item.allocation || 0), 0);
 
  let tabSelector;
  if (isMobile) {
    tabSelector = (
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Section</InputLabel>
        <Select
          value={tabValue}
          label="Section"
          onChange={(e) => setTabValue(e.target.value)}
        >
          <MenuItem value={0}>Team Structure</MenuItem>
          <MenuItem value={1}>Spending Power Management</MenuItem>
          <MenuItem value={2}>Spending Power Operations</MenuItem>
          <MenuItem value={3}>Legal Contracts</MenuItem>
          <MenuItem value={4}>Team Files</MenuItem>
        </Select>
      </FormControl>
    );
  } else {
    tabSelector = (
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          "& .MuiTab-root": {
            fontWeight: 600,
            minHeight: 56,
            fontSize: "1rem"
          }
        }}
      >
        <Tab label="Team Structure" />
        <Tab label="Spending Power Management" />
        <Tab label="Spending Power Operations" />
        <Tab label="Legal Contracts" />
        <Tab label="Team Files" />
      </Tabs>
    );
  }
 
  /* ───────────────────────────── MODERN MEMBER CARD ───────────────────────────── */
  const MemberCard = ({ member, team, showActions = true }) => {
    // For unassigned members, always show actions (no permission check needed)
    // For team members, check if user is team manager
    const canManage = showActions && (!team || isTeamManager(team));
   
    return (
      <Card sx={{
        height: '100%',
        backgroundColor:"rgb(0, 0, 0,0.02)",
        // borderRadius: { xs: , sm:  },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid',
        borderColor: member.status ? 'divider' : 'warning.light',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
          borderColor: 'primary.main',
        }
      }}>
        {/* Status indicator */}
        <Box sx={{
          position: 'absolute',
          top: { xs: -6, sm: -8 },
          right: { xs: 8, sm: 16 },
          zIndex: 1
        }}>
          <Chip
            icon={member.status ? <CheckCircleIcon /> : <WarningIcon />}
            label={member.status ? "Active" : "Pending"}
            color={member.status ? "success" : "warning"}
            size="small"
            sx={{
              fontWeight: 700,
              boxShadow: 2,
              px: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.7rem', sm: '0.8125rem' }
            }}
          />
        </Box>
        <CardContent sx={{
          p: { xs: 2, sm: 3 },
          pb: { xs: '12px !important', sm: '16px !important' }
        }}>
          <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
            <Avatar
              src={member.phphref}
              sx={{
                width: { xs: 48, sm: 64 },
                height: { xs: 48, sm: 64 },
                border: { xs: '2px solid', sm: '3px solid' },
                borderColor: member.status ? 'success.light' : 'warning.light',
                boxShadow: { xs: 2, sm: 3 },
                flexShrink: 0
              }}
            >
              {member.first_name?.[0]}{member.last_name?.[0]}
            </Avatar>
           
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {member.first_name} {member.last_name}
                </Typography>
                {team?.team_leader_id === member.id && (
                  <Tooltip title="Team Leader">
                    <AdminPanelSettingsIcon
                      color="primary"
                      sx={{ fontSize: { xs: 16, sm: 20 } }}
                    />
                  </Tooltip>
                )}
              </Box>
             
              <Stack spacing={0.3}>
                {member.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.65rem', sm: '0.75rem' }
                      }}
                    >
                      {member.email}
                    </Typography>
                  </Box>
                )}
                {member.phone_number && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    >
                      {member.phone_number}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
            {canManage && (
              <IconButton
                size="small"
                onClick={(e) => handleOpenActionMenu(e, member, team)}
                sx={{
                  height: 'fit-content',
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  backgroundColor: 'action.hover',
                  '&:hover': { backgroundColor: 'action.selected' }
                }}
              >
                <MoreVertIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            )}
          </Box>
          <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {!member.status ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => openDialog(member)}
                startIcon={<CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                fullWidth
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  py: { xs: 0.75, sm: 1 }
                }}
              >
                Activate User
              </Button>
            ) : (
              <Link
                to={`/user/${member.id}`}
                style={{ textDecoration: 'none', width: '100%' }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  startIcon={<VisibilityIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 }
                  }}
                >
                  View Profile
                </Button>
              </Link>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };
 
  return (
    <>
      <Box sx={{ width: "100%" }}>
        {/* Team Overview Stats */}
        <Card sx={{
          mb: 3,
          backgroundColor:"#F2EBD9",
          // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#662423",
          // borderRadius: 3,
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} md={2.4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 900, mb: 0.5 }}>
                    {teamStructure?.total_teams || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Active Teams
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 900, mb: 0.5 }}>
                    {teamStructure?.assigned_members || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Assigned Members
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 900, mb: 0.5 }}>
                    {teamStructure?.unassigned_count || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Unassigned
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 900, mb: 0.5 }}>
                    ${spendingSummary?.credit_summary?.total_allocated || '0'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Allocated
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 900, mb: 0.5 }}>
                    ${spendingSummary?.credit_summary?.available_for_allocation || '0'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Available
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
       
        {/* Main Content Tabs */}
        <Box sx={{  }}>
          <Box sx={{
            borderBottom: 1,
            borderColor: "divider",
            background: "linear-gradient(45deg, #f5f5f5 0%, #e3f2fd 100%)",
            p: isMobile ? 1 : 0
          }}>
            {tabSelector}
          </Box>
         
          {/* Tab 0: Team Structure */}
          {tabValue === 0 && (
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 2 : 0 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Team Structure & Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organize and manage team members for {companyName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexDirection: isMobile ? "column" : "row", width: isMobile ? "100%" : "auto" }}>
                  <Button
                    variant="contained"
                    startIcon={<CreateIcon />}
                    onClick={handleCreateTeam}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      width: isMobile ? "100%" : "auto",
                      boxShadow: 3
                    }}
                  >
                    Create Team
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<BusinessIcon />}
                    onClick={() => {
                      setLegalEntityName(companyData?.legal_entity_name || "");
                      setLegalEntityDialogOpen(true);
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      width: isMobile ? "100%" : "auto"
                    }}
                  >
                    Legal Entity
                  </Button>
                </Box>
              </Box>
             
              {/* Teams Display */}
              {teams.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon color="primary" />
                    Teams ({teams.length})
                  </Typography>
                 
                  {teams.map((team) => (
                    <Accordion
                      key={team.id}
                      sx={{
                        mb: 2,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        "&:before": { display: "none" },
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                      defaultExpanded
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: "primary.50",
                          borderRadius: 3,
                          minHeight: 72,
                          "&.Mui-expanded": {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            minHeight: 72
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3, width: "100%", pr: 2 }}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              backgroundColor: 'primary.main',
                              boxShadow: 2
                            }}
                          >
                            <GroupIcon />
                          </Avatar>
                         
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                              {team.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {team.description || "No description"}
                            </Typography>
                          </Box>
                         
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: 'flex-end' }}>
                            <Chip
                                label={`${team.members?.length || team.member_ids?.length || 0} members`}
                                size="small"
                                color="primary"
                                sx={{ fontWeight: 600 }}
                              />
                            {team.team_leader && (
                              <Chip
                                icon={<LeaderboardIcon />}
                                label={`${team.team_leader.first_name} ${team.team_leader.last_name}`}
                                size="small"
                                color="secondary"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                            <Chip
                              icon={<AttachMoneyIcon />}
                              label={`$${team.allocated_credit_line || 0}`}
                              size="small"
                              color="info"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                     
                      <AccordionDetails sx={{ p: 3, backgroundColor: 'grey.50' }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon />
                            Team Members ({team.members?.length || 0})
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleAssignUser(team)}
                              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                            >
                              Add
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditTeam(team)}
                              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AttachMoneyIcon />}
                              onClick={() => handleAllocateTeamSpending(team)}
                              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                            >
                              BlitzPay
                            </Button>
                          </Box>
                        </Box>
                       
                        {team.members && team.members.length > 0 ? (
                          <Grid container spacing={2}>
                            {team.members.map((member) => (
                              <Grid item xs={12} sm={6} md={4} key={member.id}>
                                <MemberCard member={member} team={team} />
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, border: '2px dashed', borderColor: 'divider' }}>
                            <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                              No members assigned to this team yet.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleAssignUser(team)}
                              sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}
                            >
                              Add First Member
                            </Button>
                          </Paper>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
             
              {/* Unassigned Members */}
              {unassignedMembers.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAddIcon color="warning" />
                    Unassigned Members ({unassignedMembers.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {unassignedMembers.map((member) => (
                      <Grid item xs={12} sm={6} md={4} key={member.id}>
                        <MemberCard member={member} team={null} showActions={true} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
             
              {/* Add new member card */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Link
                    to={`/register/${companyName}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Card
                      sx={{
                        
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 160,
                        border: "2px dashed",
                        borderColor: "rgb(0, 0, 0,0.02)",
                        backgroundColor: "primary.50",
                        transition: "all 0.3s ease",
                        cursor: 'pointer',
                        "&:hover": {
                          backgroundColor: "primary.100",
                          transform: "scale(1.01)",
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Avatar sx={{
                          width: 64,
                          height: 64,
                          mb: 2,
                          margin: "0 auto 16px",
                          backgroundColor: 'primary.main'
                        }}>
                          <AddIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
                          Invite Team Member
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add a new member to {companyName}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          )}
         
          {/* Tab 1: Spending Power Management */}
          {tabValue === 1 && (
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 2 : 0 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Team Spending Power Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allocate and manage spending power for individual teams
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<BalanceIcon />}
                  onClick={handleRebalanceSpending}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    width: isMobile ? "100%" : "auto",
                    boxShadow: 3
                  }}
                >
                  Rebalance Spending Power
                </Button>
              </Box>
             
              {/* Spending Summary Cards */}
              {spendingSummary && (
                <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={3}>
                    <Card sx={{
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #4caf50 0%, #81c784 100%)",
                      color: "white",
                      boxShadow: 3
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, mb: 1 }}>
                          ${spendingSummary.credit_summary.total_credit_line}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          Total Company Spending Power
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                      color: "white",
                      boxShadow: 3
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, mb: 1 }}>
                          ${spendingSummary.credit_summary.total_allocated}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          Total Allocated
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
                      color: "white",
                      boxShadow: 3
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, mb: 1 }}>
                          ${spendingSummary.credit_summary.total_used}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          Total Used
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
                      color: "white",
                      boxShadow: 3
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, mb: 1 }}>
                          ${spendingSummary.credit_summary.available_for_allocation}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          Available to Allocate
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
             
              {/* Teams Spending Overview */}
              {spendingSummary?.teams && (
                <Grid container spacing={isMobile ? 2 : 3}>
                  {spendingSummary.teams.map((team) => {
                    const utilizationPercent = team.credit_utilization_percentage || 0;
                    return (
                      <Grid item xs={12} key={team.id}>
                        <Card sx={{
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: "divider",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: 4,
                            borderColor: "primary.main"
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 2 : 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48 }}>
                                  <GroupIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {team.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {team.members?.length || team.member_ids?.length || 0} members
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={team.credit_status || 'active'}
                                color={getSpendingStatusColor(team.credit_status)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                           
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Spending Power Utilization
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {utilizationPercent.toFixed(1)}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(utilizationPercent, 100)}
                                color={getUtilizationColor(utilizationPercent)}
                                sx={{ height: 10, borderRadius: 5 }}
                              />
                            </Box>
                           
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Allocated
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                                  ${team.allocated_credit_line}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Used
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "warning.main" }}>
                                  ${team.used_credit}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Available
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "success.main" }}>
                                  ${team.available_credit}
                                </Typography>
                              </Grid>
                            </Grid>
                           
                            <Divider sx={{ my: 2 }} />
                           
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<AttachMoneyIcon />}
                                onClick={() => handleAllocateTeamSpending(team)}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                              >
                                Allocate
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<TrendingUpIcon />}
                                onClick={() => handleSpendingUsage(team, "use")}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                              >
                                Use
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AssignmentTurnedInIcon />}
                                onClick={() => handleSpendingUsage(team, "release")}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                              >
                                Release
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          )}
         
          {/* Tab 2: Spending Power Operations */}
          {tabValue === 2 && (
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 2 : 0 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Spending Power Operations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View spending power history and request changes
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => setSpendingDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    width: isMobile ? "100%" : "auto",
                    boxShadow: 3
                  }}
                >
                  Request Change
                </Button>
              </Box>
             
              {ledgerLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "grey.100" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Operation</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Change Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New Spending Power</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New Rate</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Legal Entity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {creditLedger?.map((entry) => (
                        <TableRow
                          key={entry.id}
                          sx={{
                            "&:hover": { backgroundColor: "grey.50" },
                            transition: "background-color 0.2s"
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {new Date(entry.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.operation_type}
                              color={
                                entry.operation_type === "approve" ? "success" :
                                entry.operation_type === "request" ? "warning" :
                                entry.operation_type === "deny" ? "error" : "default"
                              }
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                            ${entry.change_amount}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            ${entry.new_credit_line}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {entry.new_interest_rate}%
                          </TableCell>
                          <TableCell>{entry.reason}</TableCell>
                          <TableCell>
                            {entry.legal_entity_name_snapshot || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          )}
         
          {/* Tab 3: Legal Contracts */}
          {tabValue === 3 && (
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Legal Contracts & Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage contracts, agreements, and legal documentation
                </Typography>
              </Box>
             
              {contractsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <Grid container spacing={isMobile ? 2 : 3}>
                  {contracts?.map((contract) => (
                    <Grid item xs={12} key={contract.id}>
                      <Card sx={{
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: 4,
                          borderColor: "primary.main",
                          transform: "translateY(-2px)"
                        }
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48 }}>
                              <DescriptionIcon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {contract.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {contract.description}
                              </Typography>
                            </Box>
                            {contract.signatures && contract.signatures.length > 0 && (
                              <Chip
                                label="Signed"
                                color="success"
                                icon={<CheckCircleIcon />}
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Box>
                         
                          <Typography variant="caption" display="block" sx={{ mb: 3, color: "text.secondary", fontWeight: 500 }}>
                            Created: {new Date(contract.creation_date).toLocaleDateString()}
                          </Typography>
                         
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={() => {
                                setSelectedContract(contract);
                                setContractDialogOpen(true);
                              }}
                              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                            >
                              View
                            </Button>
                           
                            {contract.file && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => downloadContract(contract)}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                              >
                                Download
                              </Button>
                            )}
                           
                            {(!contract.signatures || contract.signatures.length === 0) && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SignatureIcon />}
                                onClick={() => {
                                  setSelectedContract(contract);
                                  setContractSignatureDialogOpen(true);
                                }}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                              >
                                Sign Contract
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          )}
         
          {/* Tab 4: Team Files */}
          {tabValue === 4 && (
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Team Files & Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access shared files and documents for your team
                </Typography>
              </Box>
             
              {filesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <Grid container spacing={isMobile ? 2 : 3}>
                  {teamFiles?.map((file) => (
                    <Grid item xs={12} sm={6} md={4} key={file.id}>
                      <Card sx={{
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        height: '100%',
                        "&:hover": {
                          boxShadow: 4,
                          borderColor: "primary.main",
                          transform: "translateY(-2px)"
                        }
                      }}>
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Avatar sx={{ backgroundColor: 'success.main', width: 48, height: 48 }}>
                              <FolderIcon />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {file.filename}
                              </Typography>
                            </Box>
                          </Box>
                         
                          <Typography variant="caption" display="block" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
                            Type: {file.file_type || "Unknown"}
                          </Typography>
                         
                          <Typography variant="caption" display="block" sx={{ mb: 3, color: "text.secondary", fontWeight: 500 }}>
                            Uploaded: {new Date(file.uploaded_at).toLocaleDateString()}
                          </Typography>
                         
                          <Box sx={{ mt: 'auto' }}>
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => window.open(file.file_url, '_blank')}
                              sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600
                              }}
                            >
                              Open File
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          )}
        </Box>
      </Box>
     
      {/* Action Menu for User Management */}
      {/* Action Menu for User Management */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              mt: 1.5,
              minWidth: 220,
              maxWidth: 280,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'visible',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 16,
                width: 12,
                height: 12,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
                borderLeft: '1px solid',
                borderTop: '1px solid',
                borderColor: 'divider',
              },
            }
          }
        }}
      >
        <MenuItem onClick={handleDeactivateUser} sx={{ py: 1.5, px: 2.5 }}>
          <ListItemIcon sx={{ minWidth: 44 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BlockIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Deactivate User"
            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.938rem' }}
          />
        </MenuItem>
        {selectedMember?.teamId && (
          <MenuItem onClick={handleRemoveUserFromTeam} sx={{ py: 1.5, px: 2.5 }}>
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Remove from Team"
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.938rem' }}
            />
          </MenuItem>
        )}
      </Menu>
     
      {/* Deactivate User Dialog */}
      {/* Deactivate User Dialog - POSITIONED NEAR ACTION MENU */}
      <Dialog 
        open={deactivateDialogOpen} 
        onClose={() => setDeactivateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'stretch' : 'flex-start',
          },
          '& .MuiDialog-paper': isMobile ? {} : {
            position: 'absolute',
            top: '20%',
            m: 0,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          bgcolor: 'warning.50',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <BlockIcon color="warning" sx={{ fontSize: 28 }} />
          Deactivate User Account
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This action will revoke the user's access to the platform and company resources.
          </Alert>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
            Are you sure you want to deactivate{" "}
            <Box component="span" sx={{ color: 'warning.dark' }}>
              {selectedMember?.first_name} {selectedMember?.last_name}
            </Box>
            ?
          </Typography>
          
          <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              Common use cases:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Unrecognized user (possible unauthorized access)
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Employee termination or departure
                </Typography>
              </li>
            </Box>
          </Box>

          <Box sx={{ bgcolor: 'error.50', borderRadius: 2, p: 2, border: '1px solid', borderColor: 'error.light' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: 'error.dark' }}>
              This action will:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <li>
                <Typography variant="body2" color="error.dark">
                  Remove them from all teams
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="error.dark">
                  Remove them from the company
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="error.dark">
                  Revoke spending power access
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="error.dark">
                  Send notification to all company members
                </Typography>
              </li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setDeactivateDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmDeactivateUser}
            disabled={deactivateUserMutation.isLoading}
            startIcon={deactivateUserMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {deactivateUserMutation.isLoading ? "Deactivating..." : "Deactivate User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove User Dialog - POSITIONED */}
      <Dialog 
        open={removeDialogOpen} 
        onClose={() => setRemoveDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'stretch' : 'flex-start',
          },
          '& .MuiDialog-paper': isMobile ? {} : {
            position: 'absolute',
            top: '20%',
            m: 0,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          bgcolor: 'info.50',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <DeleteOutlineIcon color="error" sx={{ fontSize: 28 }} />
          Remove from Team
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            This will remove the user from their current team assignment.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
            Are you sure you want to remove{" "}
            <Box component="span" sx={{ color: 'error.main' }}>
              {selectedMember?.first_name} {selectedMember?.last_name}
            </Box>
            {" "}from this team?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
            The user will be moved to unassigned members and can be reassigned to another team.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setRemoveDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRemoveUser}
            disabled={removeUserMutation.isLoading}
            startIcon={removeUserMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {removeUserMutation.isLoading ? "Removing..." : "Remove from Team"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* User Activation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm team-member activation?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            By confirming{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            , you authorize them to draw against your company's shared spending power.
          </Typography>
          <Typography variant="caption" component="p">
            View full terms{" "}
            <MuiLink component={Link} to="/terms" target="_blank">
              here
            </MuiLink>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => activateMutation.mutate(selectedUser.id)}
            disabled={activateMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {activateMutation.isLoading ? "Activating..." : "Agree & Activate"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Team Create/Edit Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingTeam ? "Edit Team" : "Create New Team"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={teamFormData.name}
                onChange={(e) => setTeamFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={teamFormData.description}
                onChange={(e) => setTeamFormData(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Team Leader</InputLabel>
                <Select
                  value={teamFormData.team_leader_id}
                  onChange={(e) => setTeamFormData(prev => ({ ...prev, team_leader_id: e.target.value }))}
                  label="Team Leader"
                >
                  <MenuItem value="">No Leader</MenuItem>
                  {allMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTeamSubmit}
            disabled={createTeamMutation.isLoading || updateTeamMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {createTeamMutation.isLoading || updateTeamMutation.isLoading ?
              "Saving..." : (editingTeam ? "Update Team" : "Create Team")
            }
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* User Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Assign User to {selectedTeamForAssignment?.name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserForAssignment}
              onChange={(e) => setSelectedUserForAssignment(e.target.value)}
              label="Select User"
            >
              {unassignedMembers.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.first_name} {member.last_name} - {member.status ? "Active" : "Pending"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignSubmit}
            disabled={assignUserMutation.isLoading || !selectedUserForAssignment}
            sx={{ fontWeight: 600 }}
          >
            {assignUserMutation.isLoading ? "Assigning..." : "Assign User"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Team Spending Allocation Dialog */}
      <Dialog open={teamSpendingDialogOpen} onClose={() => setTeamSpendingDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Allocate Spending Power to {selectedTeamForSpending?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allocated Spending Power"
                type="number"
                value={teamSpendingData.allocated_credit_line}
                onChange={(e) => setTeamSpendingData(prev => ({
                  ...prev,
                  allocated_credit_line: e.target.value
                }))}
                helperText={`Current: $${selectedTeamForSpending?.allocated_credit_line || 0} | Used: $${selectedTeamForSpending?.used_credit || 0}`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Allocation"
                multiline
                rows={3}
                value={teamSpendingData.reason}
                onChange={(e) => setTeamSpendingData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
            {spendingSummary && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Company Spending Power: ${spendingSummary.credit_summary.total_credit_line}<br/>
                  Currently Available: ${spendingSummary.credit_summary.available_for_allocation}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamSpendingDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTeamSpendingSubmit}
            disabled={allocateTeamSpendingMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {allocateTeamSpendingMutation.isLoading ? "Allocating..." : "Allocate Spending Power"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Spending Usage/Release Dialog */}
      <Dialog open={spendingUsageDialogOpen} onClose={() => setSpendingUsageDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {spendingUsageData.operation === "use" ? "Use Team Spending Power" : "Release Team Spending Power"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={spendingUsageData.amount}
                onChange={(e) => setSpendingUsageData(prev => ({
                  ...prev,
                  amount: e.target.value
                }))}
                helperText={
                  spendingUsageData.operation === "use"
                    ? `Available: $${selectedTeamForSpending?.available_credit || 0}`
                    : `Currently Used: $${selectedTeamForSpending?.used_credit || 0}`
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={spendingUsageData.reason}
                onChange={(e) => setSpendingUsageData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpendingUsageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSpendingUsageSubmit}
            disabled={manageSpendingUsageMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {manageSpendingUsageMutation.isLoading ? "Processing..." :
             (spendingUsageData.operation === "use" ? "Use Spending Power" : "Release Spending Power")
            }
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Spending Rebalancing Dialog */}
      <Dialog open={rebalanceDialogOpen} onClose={() => setRebalanceDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Rebalance Team Spending Power Allocations
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Adjust spending power allocations across teams. Total allocation cannot exceed company's total spending power.
            </Typography>
            {spendingSummary && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Company Spending Power: ${spendingSummary.credit_summary.total_credit_line} |
                New Total: ${totalAllocationAmount.toFixed(2)} |
                Remaining: ${(parseFloat(spendingSummary.credit_summary.total_credit_line) - totalAllocationAmount).toFixed(2)}
              </Typography>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Used</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>New Allocation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rebalanceData.map((item, index) => (
                  <TableRow key={item.team_id}>
                    <TableCell>{item.team_name}</TableCell>
                    <TableCell>${item.current_allocation}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        ${item.used_credit}
                        {item.allocation < item.used_credit && (
                          <Tooltip title="Allocation cannot be less than used spending power">
                            <WarningIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.allocation}
                        onChange={(e) => {
                          const newData = [...rebalanceData];
                          newData[index].allocation = parseFloat(e.target.value) || 0;
                          setRebalanceData(newData);
                        }}
                        error={item.allocation < item.used_credit}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebalanceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRebalanceSubmit}
            disabled={
              rebalanceSpendingMutation.isLoading ||
              (spendingSummary && totalAllocationAmount > parseFloat(spendingSummary.credit_summary.total_credit_line)) ||
              rebalanceData.some(item => item.allocation < item.used_credit)
            }
            sx={{ fontWeight: 600 }}
          >
            {rebalanceSpendingMutation.isLoading ? "Rebalancing..." : "Apply Rebalancing"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Spending Power Request Dialog */}
      <Dialog open={spendingDialogOpen} onClose={() => setSpendingDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Request Spending Power Change
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested Spending Power"
                type="number"
                value={spendingRequestData.requested_credit_line}
                onChange={(e) => setSpendingRequestData(prev => ({
                  ...prev,
                  requested_credit_line: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested Discount Rate (%)"
                type="number"
                value={spendingRequestData.requested_interest_rate}
                onChange={(e) => setSpendingRequestData(prev => ({
                  ...prev,
                  requested_interest_rate: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Change"
                multiline
                rows={3}
                value={spendingRequestData.reason}
                onChange={(e) => setSpendingRequestData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpendingDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSpendingRequest}
            disabled={spendingRequestMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {spendingRequestMutation.isLoading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Legal Entity Dialog */}
      <Dialog open={legalEntityDialogOpen} onClose={() => setLegalEntityDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Update Legal Entity Name
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Legal Entity Name"
            value={legalEntityName}
            onChange={(e) => setLegalEntityName(e.target.value)}
            sx={{ mt: 2 }}
            helperText="This will be used in all future contracts and legal documents"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegalEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleLegalEntityUpdate}
            disabled={updateLegalEntityMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {updateLegalEntityMutation.isLoading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Contract View Dialog */}
      <Dialog open={contractDialogOpen} onClose={() => setContractDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedContract?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{
            whiteSpace: "pre-wrap",
            p: 3,
            bgcolor: "grey.50",
            borderRadius: 2,
            lineHeight: 1.6
          }}>
            {selectedContract?.contract_body}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
     
      {/* Contract Signature Dialog */}
      <Dialog open={contractSignatureDialogOpen} onClose={() => setContractSignatureDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Sign Contract
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            By clicking "Sign Contract", you agree to the terms and conditions outlined in "{selectedContract?.name}".
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your signature will be timestamped and legally binding.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractSignatureDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleContractSign}
            disabled={signContractMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {signContractMutation.isLoading ? "Signing..." : "Sign Contract"}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyTeam;